/*
 * Copyright © 2025 Gavin Sawyer. All rights reserved.
 */

import { inject, Injectable, type Signal }                                                                                                              from "@angular/core";
import { toSignal }                                                                                                                                     from "@angular/core/rxjs-interop";
import { type User }                                                                                                                                    from "@angular/fire/auth";
import { addDoc, collection, collectionData, type CollectionReference, type DocumentReference, type DocumentSnapshot, Firestore, getDoc, query, where } from "@angular/fire/firestore";
import { type StripeSetupIntentDocument }                                                                                                               from "@bowstring/interfaces";
import { catchError, from, Observable, of, switchMap }                                                                                                  from "rxjs";
import { AuthenticationService }                                                                                                                        from "./AuthenticationService";
import { RxSsrService }                                                                                                                                 from "./RxSsrService";


@Injectable({ providedIn: "root" })
export class StripeSetupIntentService {

  private readonly authenticationService: AuthenticationService = inject<AuthenticationService>(AuthenticationService);
  private readonly firestore: Firestore                         = inject<Firestore>(Firestore);
  private readonly rxSsrService: RxSsrService                   = inject<RxSsrService>(RxSsrService);

  public readonly stripeSetupIntentDocument$: Signal<StripeSetupIntentDocument | undefined> = toSignal<StripeSetupIntentDocument | undefined>(
    this.authenticationService.userObservable.pipe<StripeSetupIntentDocument | undefined>(
      this.rxSsrService.wrap<User, StripeSetupIntentDocument | undefined>(
        switchMap<User, Observable<StripeSetupIntentDocument | undefined>>(
          ({ uid: userId }: User): Observable<StripeSetupIntentDocument | undefined> => collectionData<StripeSetupIntentDocument>(
            query<StripeSetupIntentDocument, StripeSetupIntentDocument>(
              collection(
                this.firestore,
                "stripeSetupIntents",
              ) as CollectionReference<StripeSetupIntentDocument, StripeSetupIntentDocument>,
              where(
                "userId",
                "==",
                userId,
              ),
            ),
          ).pipe<Array<StripeSetupIntentDocument> | undefined, StripeSetupIntentDocument | undefined>(
            catchError<Array<StripeSetupIntentDocument>, Observable<undefined>>(
              (): Observable<undefined> => of<undefined>(undefined),
            ),
            switchMap<Array<StripeSetupIntentDocument> | undefined, Observable<StripeSetupIntentDocument | undefined>>(
              (stripeSetupIntentDocuments?: Array<StripeSetupIntentDocument>): Observable<StripeSetupIntentDocument | undefined> => !stripeSetupIntentDocuments ? of<undefined>(undefined) : stripeSetupIntentDocuments.length ? of<StripeSetupIntentDocument | undefined>(
                stripeSetupIntentDocuments[0],
              ) : from<Promise<StripeSetupIntentDocument | undefined>>(
                addDoc<StripeSetupIntentDocument, StripeSetupIntentDocument>(
                  collection(
                    this.firestore,
                    "stripeSetupIntents",
                  ) as CollectionReference<StripeSetupIntentDocument, StripeSetupIntentDocument>,
                  {
                    updateOrigin: "firestore",
                    userId,
                  },
                ).then<StripeSetupIntentDocument | undefined, never>(
                  (stripeSetupIntentDocumentReference: DocumentReference<StripeSetupIntentDocument, StripeSetupIntentDocument>): Promise<StripeSetupIntentDocument | undefined> => getDoc<StripeSetupIntentDocument, StripeSetupIntentDocument>(stripeSetupIntentDocumentReference).then<StripeSetupIntentDocument | undefined, never>(
                    (stripeSetupIntentDocumentSnapshot: DocumentSnapshot<StripeSetupIntentDocument, StripeSetupIntentDocument>): StripeSetupIntentDocument | undefined => stripeSetupIntentDocumentSnapshot.data(),
                    (error: Error): never => {
                      console.error("Something went wrong.");

                      throw error;
                    },
                  ),
                  (error: Error): never => {
                    console.error("Something went wrong.");

                    throw error;
                  },
                ),
              ),
            ),
          ),
        ),
        "01967975-c7e2-71fe-aa38-2e7fd2d4dcd9",
      ),
    ),
  );

}
